import { Card, Col, Flex, Form, Row, Select, Switch, Typography } from "antd";
import React, { useEffect } from "react";
import {
  numberFormatOptions,
  temperatureOptions,
  timeFormatOptions,
  firstDayOptions,
  dateFormatOptions,
  measurementOptions,
  calendarOptions,
  roundingOptionsObject,
} from "../../DummyData";
import { useSelector, useDispatch } from "react-redux";
import {
  getEntityFormatsAction,
  createEntityFormatsAction,
  getSingleEntityDetailsAction,
} from "../../../../../../../store/action/admin/entityActions";

const { Text } = Typography;

const FormatsS2 = React.forwardRef(({ entityId }, ref) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const getEntityFormatsList = useSelector(
    (state) => state.getEntityFormatsList
  );

  // Load existing formats when component mounts
  useEffect(() => {
    if (entityId) {
      dispatch(getEntityFormatsAction(entityId));
    }
  }, [entityId, dispatch]);

  useEffect(() => {
    if (getEntityFormatsList?.info) {
      form.setFieldsValue({
        timeFormat: getEntityFormatsList.info.timeFormat,
        firstDay: getEntityFormatsList.info.firstDayOfWeek,
        dateFormat: getEntityFormatsList.info.dateFormat,
        measurement: getEntityFormatsList.info.measurementSystem,
        temperature: getEntityFormatsList.info.temperatureUnit,
        calendar: getEntityFormatsList.info.calendarSystem,
        numberFormat: getEntityFormatsList.info.numberFormat,
        enableRoundingOff: getEntityFormatsList.info.roundingOff || false,
        roundingOption: getEntityFormatsList.info.roundingRule,
      });
    }
  }, [getEntityFormatsList, form]);

  React.useImperativeHandle(ref, () => ({
    getValues: () => ({
      ...(values || {}),
      ...(form.getFieldsValue(true) || {}),
    }),
    setValues: (newValues) => {
      form.setFieldsValue(newValues);
    },
    resetForm: () => {
      form.resetFields();
    },
    submit: async () => {
      try {
        const formData = form.getFieldsValue(true);

        if (!entityId) return formData;

        const apiData = {
          timeFormat: formData.timeFormat,
          firstDayOfWeek: formData.firstDay,
          dateFormat: formData.dateFormat,
          measurementSystem: formData.measurement,
          temperatureUnit: formData.temperature,
          calendarSystem: formData.calendar,
          numberFormat: formData.numberFormat,
          roundingOff: formData.enableRoundingOff,
          roundingRule: formData.roundingOption,
        };

        try {
          await dispatch(createEntityFormatsAction(entityId, apiData));
          await dispatch(getSingleEntityDetailsAction(entityId));
        } catch (error) {
          console.error("Failed to update entity formats:", error);

          const errorMessage =
            error?.message ||
            error?.response?.data?.message ||
            error?.response?.message;

          if (errorMessage) {
            await dispatch(createEntityFormatsAction(entityId, apiData));
          } else {
            throw error;
          }
        }

        return formData;
      } catch (error) {
        console.error("Failed to save entity formats:", error);
        throw error;
      }
    },
  }));

  const descriptionStyle = { fontSize: 12, color: "#A8A8A8", display: "block" };

  const roundingOptions = Object.entries(roundingOptionsObject).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        timeFormat: undefined,
        firstDay: undefined,
        dateFormat: undefined,
        measurement: undefined,
        temperature: undefined,
        calendar: undefined,
        numberFormat: undefined,
        enableRoundingOff: false,
        roundingOption: undefined,
      }}
    >
      <Row gutter={[16, 16]}>
        {/* 🔹 Left Column - Format Settings */}
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Format Settings
              </div>
            }
          >
            <Flex gap={16} vertical>
              <Flex gap={16} align="center">
                {/* Time Format */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="timeFormat"
                    label={timeFormatOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={timeFormatOptions.options}
                      placeholder="Select time format"
                      onChange={(v) => form.setFieldValue("timeFormat", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {timeFormatOptions.description}
                  </Text>
                </div>

                {/* First Day */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="firstDay"
                    label={firstDayOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={firstDayOptions.options}
                      placeholder="Select first day of the week"
                      onChange={(v) => form.setFieldValue("firstDay", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {firstDayOptions.description}
                  </Text>
                </div>
              </Flex>

              <Flex gap={16} align="center">
                {/* Date Format */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="dateFormat"
                    label={dateFormatOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={dateFormatOptions.options}
                      placeholder="Select date format"
                      onChange={(v) => form.setFieldValue("dateFormat", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {dateFormatOptions.description}
                  </Text>
                </div>

                {/* Measurement */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="measurement"
                    label={measurementOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={measurementOptions.options}
                      placeholder="Select measurement system"
                      onChange={(v) => form.setFieldValue("measurement", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {measurementOptions.description}
                  </Text>
                </div>
              </Flex>
              <Flex gap={16} align="center">
                {/* Temperature */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="temperature"
                    label={temperatureOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={temperatureOptions.options}
                      placeholder="Select temperature unit"
                      onChange={(v) => form.setFieldValue("temperature", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {temperatureOptions.description}
                  </Text>
                </div>

                {/* Calendar */}
                <div style={{ width: "50%" }}>
                  <Form.Item
                    name="calendar"
                    label={calendarOptions.label}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      options={calendarOptions.options}
                      placeholder="Select calendar type"
                      onChange={(v) => form.setFieldValue("calendar", v)}
                    />
                  </Form.Item>
                  <Text style={descriptionStyle}>
                    {calendarOptions.description}
                  </Text>
                </div>
              </Flex>

              {/* Number Format */}
              <div style={{ width: "49%" }}>
                <Form.Item
                  name="numberFormat"
                  label={numberFormatOptions.label}
                  style={{ padding: 0, margin: 0 }}
                >
                  <Select
                    options={numberFormatOptions.options}
                    placeholder="Select number format"
                    onChange={(v) => form.setFieldValue("numberFormat", v)}
                  />
                </Form.Item>
                <Text style={descriptionStyle}>
                  {numberFormatOptions.description}
                </Text>
              </div>
            </Flex>
          </Card>
        </Col>

        {/* 🔹 Right Column - Rounding Off */}
        <Col span={12}>
          <Card
            title={
              <div
                style={{
                  borderBottom: themeDefault?.info?.newConfig?.cardborder,
                  paddingBottom: 8,
                }}
              >
                Rounding Off
              </div>
            }
          >
            <Flex vertical gap={12}>
              <Flex align="center" gap={8}>
                <Form.Item
                  name="enableRoundingOff"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </Form.Item>
                <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                  Round off all values to the nearest digit
                </Text>
              </Flex>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Eg: If the net pay value is 2.45, it will be rounded to 2 and
                2.65 will be rounded to 3
              </Text>

              {/* Rounding Option */}
              <div>
                <Form.Item name="roundingOption" noStyle>
                  <Select
                    placeholder="Select rounding option"
                    options={roundingOptions}
                    style={{ width: "100%" }}
                    onChange={(v) => form.setFieldValue("roundingOption", v)}
                  />
                </Form.Item>
              </div>
            </Flex>
          </Card>
        </Col>
      </Row>
    </Form>
  );
});

FormatsS2.displayName = "FormatsS2";

export default FormatsS2;
